import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { useDesignMode } from '../contexts/DesignContext';
import { SketchStickyNote, SketchProgressBar } from './sketchy';
import { classNames } from '../utils/classNames';

interface UploadProgressDisplayProps {
  uploadingFiles: Set<string>;
  uploadProgress: Record<string, { filename: string; progress: number }>;
  uploadFilenames: Record<string, string>;
  uploadFileStatuses?: Record<string, 'uploading' | 'completed' | 'failed'>;
}

const UploadProgressDisplay: React.FC<UploadProgressDisplayProps> = ({
  uploadingFiles,
  uploadProgress,
  uploadFilenames,
  uploadFileStatuses = {},
}) => {
  const { t } = useTranslation();
  const { isSketchy } = useDesignMode();

  // Get overall progress (assuming single upload ID for overall progress)
  const uploadId = uploadingFiles.size > 0 ? Array.from(uploadingFiles)[0] : null;
  const progressInfo = uploadId ? uploadProgress[uploadId] : null;
  const actualProgress = progressInfo?.progress ?? 0;

  if (uploadingFiles.size === 0) {
    return null;
  }

  const hasProgress = progressInfo?.progress !== undefined;
  const progress = Math.round(actualProgress);

  // Extract file list from uploadFileStatuses
  const fileList = Object.keys(uploadFileStatuses).sort();

  const Wrapper = isSketchy ? SketchStickyNote : 'div';

  return (
    <Wrapper
      className={classNames(
        'mb-4 p-5 rounded-lg border',
        !isSketchy && 'bg-[var(--info-yellow-bg)] border-[var(--info-yellow-border)]'
      )}
    >
      <div className="flex items-center justify-between mb-4">
        <span className="text-base font-semibold text-[var(--info-yellow-text)]">
          {t('fileUpload.uploading')}
        </span>
        {hasProgress && progress > 0 && progress < 100 && (
          <span className="text-base font-semibold whitespace-nowrap text-[var(--info-yellow-text)]">
            {progress}%
          </span>
        )}
      </div>
      
      {/* Overall progress bar with smooth animation */}
      {hasProgress && progress > 0 && (
        <SketchProgressBar
          value={actualProgress}
          className="mb-3"
          data-testid="upload-progress-bar"
        />
      )}
      
      {/* File list with status indicators */}
      {fileList.length > 0 && (
        <div className="space-y-2 mb-3">
          {fileList.map((fileName) => {
            const status = uploadFileStatuses[fileName];
            return (
              <div
                key={fileName}
                className="flex items-center gap-2 text-sm"
                style={{ color: 'var(--info-yellow-text)' }}
              >
                {status === 'completed' && (
                  <CheckCircleIcon className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--info-green-text)' }} />
                )}
                {status === 'failed' && (
                  <XCircleIcon className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--info-red-text)' }} />
                )}
                {status === 'uploading' && (
                  <div className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-[var(--info-yellow-text)] animate-pulse" />
                  </div>
                )}
                <span className="truncate flex-1" style={{ 
                  opacity: status === 'completed' ? 0.7 : status === 'failed' ? 0.7 : 1 
                }}>
                  {fileName}
                </span>
              </div>
            );
          })}
        </div>
      )}
      
      <p className="text-xs text-[var(--info-yellow-text)]">
        {t('fileUpload.willAppearAutomatically')}
      </p>
    </Wrapper>
  );
};

export default UploadProgressDisplay;
